import { useState, useEffect } from 'react';
import { useAuthToken } from '../../../../hooks/useAuthToken';
import { useResponsive } from '../../../../hooks/useResponsive';
import { supabaseApi } from '../../../../lib/api/supabase';
import { SupabaseConfigResponse } from '@optidev/shared-types';
import toast from 'react-hot-toast';
import { Copy, Check, Database } from 'lucide-react';

import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { ConnectionStringModal } from '../../../../components/supabase/ConnectionStringModal';
import { cn } from '../../../../lib/utils';

interface ConnectionInfoTabProps {
  projectId: string;
}

export function ConnectionInfoTab({ projectId }: ConnectionInfoTabProps) {
  const { token } = useAuthToken();
  const { isMobile } = useResponsive();
  const [config, setConfig] = useState<SupabaseConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showConnectionModal, setShowConnectionModal] = useState(false);

  useEffect(() => {
    if (token) {
      loadConfig();
    }
  }, [projectId, token]);

  // Auto-refresh while provisioning
  useEffect(() => {
    if (!config || config.status !== 'PROVISIONING') return;

    const interval = setInterval(() => {
      loadConfig();
    }, 3000); // Check every 3 seconds

    return () => clearInterval(interval);
  }, [config?.status]);

  const loadConfig = async () => {
    if (!token) return;

    try {
      const data = await supabaseApi.getProjectConfig(token, projectId);
      setConfig(data);
    } catch (error) {
      console.error('Failed to load config:', error);
      toast.error('Failed to load connection configuration');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm text-muted-foreground">Loading configuration...</div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm text-muted-foreground">
          Failed to load configuration
        </div>
      </div>
    );
  }

  const CopyButton = ({ value, fieldName }: { value: string; fieldName: string }) => (
    <button
      onClick={() => copyToClipboard(value, fieldName)}
      className="shrink-0 p-1.5 opacity-0 group-hover:opacity-100 hover:bg-muted rounded transition-all"
      title="Copy to clipboard"
      disabled={!value}
    >
      {copiedField === fieldName ? (
        <Check className="h-3.5 w-3.5 text-primary" />
      ) : (
        <Copy className="h-3.5 w-3.5 text-muted-foreground" />
      )}
    </button>
  );

  const ValueDisplay = ({ value, masked = false }: { value: string; masked?: boolean }) => (
    <div className="font-mono text-sm text-foreground break-all select-all">
      {!value ? (
        <span className="text-muted-foreground italic font-sans select-auto">Fetching from OptiDev Cloud...</span>
      ) : masked ? (
        '••••••••' + value.slice(-8)
      ) : (
        value
      )}
    </div>
  );

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold mb-1">Connection Information</h3>
        <p className="text-sm text-muted-foreground">
          Credentials for connecting your application to OptiDev Cloud
        </p>
      </div>

      {/* Provisioning Notice */}
      {config.status === 'PROVISIONING' && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-sm font-medium text-primary">
              Provisioning OptiDev Cloud Project
            </p>
          </div>
          <p className="text-sm text-primary/80 mt-2">
            Your project is being created. Connection details will appear automatically when ready.
          </p>
        </div>
      )}

      {/* Single credentials card */}
      <div className="rounded-lg border border-border/60 divide-y divide-border/40">
        {/* API URL */}
        <div className="group px-5 py-4 flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
              API URL
            </div>
            <ValueDisplay value={config.gatewayUrl || config.apiUrl} />
          </div>
          <CopyButton value={config.gatewayUrl || config.apiUrl} fieldName="apiUrl" />
        </div>

        {/* Publishable Key */}
        <div className="group px-5 py-4 flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
              Publishable Key
            </div>
            <ValueDisplay value={config.publishableKey} />
          </div>
          <CopyButton value={config.publishableKey} fieldName="publishableKey" />
        </div>

        {/* Database Connection */}
        <div className="group px-5 py-4 flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 mb-1.5">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Database
              </span>
              <Button
                size="sm"
                variant="outline"
                className="h-6 text-xs px-2.5"
                onClick={() => setShowConnectionModal(true)}
                disabled={!config.connectionString}
              >
                <Database className="h-3 w-3 mr-1.5" />
                Connect
              </Button>
            </div>
            <ValueDisplay value={config.connectionString} masked />
          </div>
          <CopyButton value={config.connectionString} fieldName="connectionString" />
        </div>

        {/* Footer: Region + Status */}
        {(config.region || config.status) && (
          <div className={cn(
            "px-5 py-3 flex items-center bg-muted/20",
            isMobile ? "gap-4 flex-wrap" : "gap-8"
          )}>
            {config.region && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Region</span>
                <span className="font-medium">{config.region}</span>
              </div>
            )}
            {config.status && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Status</span>
                <Badge variant={config.status === 'ACTIVE_HEALTHY' ? 'default' : 'secondary'}>
                  {config.status.toLowerCase().replace('_', ' ')}
                </Badge>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Connection String Modal */}
      <ConnectionStringModal
        isOpen={showConnectionModal}
        onClose={() => setShowConnectionModal(false)}
        connectionString={config.connectionString}
      />
    </div>
  );
}
